/**
 * Locale-aware money, number and duration formatting.
 *
 * This exists because the app had **two** money helpers and neither was
 * locale-aware:
 *
 *   - `utils/format.ts#formatCurrency` — `Intl` currency style, hardcoded
 *     `'en-US'`.
 *   - `utils/money.ts#formatMoney` — `"1,234.50 EGP"`, hardcoded `'en-US'`.
 *
 * Both render Latin digits and English symbol placement under the Arabic
 * switcher, so an Arabic clinic saw `1,234.50 EGP` in an RTL page. The third
 * pattern — bare `.toFixed(2)` — dropped the currency entirely.
 *
 * The helpers here take the active locale and format through `Intl`, so
 * Arabic gets Arabic-Indic digits and the currency in its conventional
 * position. They deliberately do NOT replace the existing helpers at their
 * ~200 call sites; those are marked `@deprecated` and migrate opportunistically.
 *
 * ## Bidi safety
 *
 * `formatMoneyIntl` wraps its output in U+2068/U+2069 (FSI/PDI) when asked.
 * Inside an RTL paragraph an unwrapped `1500.00 EGP` can reorder so the
 * currency lands on the wrong side of the number, or a trailing `.` jumps to
 * the front. Isolating the run pins it. `<Money>` in `components/ds` does this
 * for you; reach for the raw helper only outside React.
 */

import { setDefaultOptions } from 'date-fns';
import { ar as dateFnsAr } from 'date-fns/locale/ar';
import { de as dateFnsDe } from 'date-fns/locale/de';
import { enUS as dateFnsEn } from 'date-fns/locale/en-US';
import { fr as dateFnsFr } from 'date-fns/locale/fr';

/** Before the clinic loads. Kept local: utils/money imports this module. */
const DEFAULT_CURRENCY = 'EGP';

export type MoneyLocale = string;

/** Coerce the API's `string | number | null` money fields to a real number. */
export function toAmount(value: number | string | null | undefined): number {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return n === undefined || n === null || Number.isNaN(n) ? 0 : n;
}

/** U+2068 FIRST STRONG ISOLATE … U+2069 POP DIRECTIONAL ISOLATE. */
function isolate(s: string): string {
  return `⁨${s}⁩`;
}

/**
 * The number of decimals a currency is written with (ISO 4217): EGP/USD 2,
 * KWD/BHD/OMR/JOD 3, JPY 0. Money is always shown at its currency's own
 * precision — a Kuwaiti amount rounded to 2 places is a wrong amount, and a
 * yen amount with ".00" is a made-up one.
 */
export function currencyFractionDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * What an unqualified amount is formatted as, set once from React context
 * (`<DisplayContextSync/>`): the active language, and the currency of the
 * current scope — the selected branch's, or the clinic base on "All branches".
 * Read by the legacy helpers (`formatMoney`, `formatCurrency`, `formatEGP`)
 * that cannot call hooks, so every money string in the app follows one rule.
 */
const displayContext = { locale: 'en', currency: DEFAULT_CURRENCY };
const DATE_FNS_LOCALES = {
  ar: dateFnsAr,
  de: dateFnsDe,
  en: dateFnsEn,
  fr: dateFnsFr,
};
export function setDisplayContext(ctx: { locale?: string; currency?: string }) {
  if (ctx.locale && ctx.locale !== displayContext.locale) {
    displayContext.locale = ctx.locale;
    // Every date-fns `format` / `formatDistance` in the app follows the
    // language from here, so month and weekday names are never left in
    // English. The week start is pinned: a locale must not move the first
    // column of a calendar or the edge of a "this week" range.
    const lang = ctx.locale.split('-')[0] as keyof typeof DATE_FNS_LOCALES;
    setDefaultOptions({
      locale: DATE_FNS_LOCALES[lang] ?? dateFnsEn,
      weekStartsOn: 0,
      firstWeekContainsDate: 1,
    });
  }
  if (ctx.currency) displayContext.currency = ctx.currency;
}

/**
 * The `Intl` locale for a date, outside React (`useFormatters().date` inside).
 * English keeps day-month order; every other language uses its own.
 */
export function dateLocale(): string {
  return displayContext.locale.startsWith('en')
    ? 'en-GB'
    : displayContext.locale;
}

/** The `Intl` locale for a time of day: 12-hour "9:45 AM" in English. */
export function timeLocale(): string {
  return displayContext.locale.startsWith('en')
    ? 'en-US'
    : displayContext.locale;
}

/** The `Intl` locale for a plain number (axis ticks, counts). */
export function numberLocale(): string {
  return displayContext.locale;
}
export function getDisplayContext(): Readonly<typeof displayContext> {
  return displayContext;
}

export interface FormatMoneyOptions {
  locale?: MoneyLocale;
  currency?: string;
  /** Both bounds default to the currency's own decimals (2 for EGP). */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Renders `−`/`+` explicitly; zero stays unsigned. */
  signed?: boolean;
  /** Wrap in bidi isolates. Default true — see the note above. */
  bidiIsolate?: boolean;
}

/**
 * The one money formatter.
 *
 *   formatMoneyIntl(1234.5, { locale: 'en' })  → "EGP 1,234.50"
 *   formatMoneyIntl(1234.5, { locale: 'ar' })  → "‏١٬٢٣٤٫٥٠ ج.م.‏"
 *   formatMoneyIntl(-40, { signed: true })     → "−EGP 40.00"
 */
export function formatMoneyIntl(
  value: number | string | null | undefined,
  {
    locale = displayContext.locale,
    currency = displayContext.currency,
    minimumFractionDigits,
    maximumFractionDigits,
    signed = false,
    bidiIsolate = true,
  }: FormatMoneyOptions = {},
): string {
  const digits = currencyFractionDigits(currency);
  minimumFractionDigits ??= digits;
  maximumFractionDigits ??= Math.max(digits, minimumFractionDigits);
  const amount = toAmount(value);
  const magnitude = signed ? Math.abs(amount) : amount;

  let body: string;
  try {
    body = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(magnitude);
  } catch {
    // An unknown currency code (a clinic typo, a new market) must not blank
    // the number — the amount matters more than its symbol.
    body = `${magnitude.toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    })} ${currency}`;
  }

  if (signed && amount !== 0) {
    body = `${amount > 0 ? '+' : '−'}${body}`;
  }

  return bidiIsolate ? isolate(body) : body;
}

/** Plain number with the locale's own digits and grouping. */
export function formatNumberIntl(
  value: number | string | null | undefined,
  {
    locale = 'en',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  }: {
    locale?: MoneyLocale;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string {
  return toAmount(value).toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

/**
 * A duration in minutes, as a human phrase.
 *
 * Replaces three private copies (`home/homeFormat`, `domain/queue-actions`,
 * `pages/VerifyEmail`), none of which was translatable.
 *
 * Returns the *parts*, not a string, because the unit words have to come from
 * i18n — building "1 h 5 min" here would hardcode English into a formatter.
 * Callers pass the parts to the `common.duration.*` keys — `hoursMinutes`,
 * `hours` or `minutes` depending on which are non-zero. `useFormatters().duration`
 * already does that and is what a component should call.
 */
export interface DurationParts {
  hours: number;
  minutes: number;
  /** Total, rounded to whole minutes. Useful for `<= 0` checks. */
  totalMinutes: number;
}

export function durationParts(
  totalMinutes: number | null | undefined,
): DurationParts | null {
  if (totalMinutes === null || totalMinutes === undefined) return null;
  const total = Math.max(0, Math.round(totalMinutes));
  return {
    hours: Math.floor(total / 60),
    minutes: total % 60,
    totalMinutes: total,
  };
}

/**
 * Minutes between two instants, or null if either is missing/invalid.
 * Negative spans (a clock skew, a bad backfill) return null rather than a
 * nonsense "−12 min" on a clinical record.
 */
export function minutesBetween(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): number | null {
  if (!start || !end) return null;
  const a = typeof start === 'string' ? new Date(start) : start;
  const b = typeof end === 'string' ? new Date(end) : end;
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const mins = Math.round((b.getTime() - a.getTime()) / 60_000);
  return mins >= 0 ? mins : null;
}
